import { NumberDataFlowType, BooleanDataFlowType, StringDataFlowType, TimeStampDataFlowType } from 'app/model/dataflow/dataflow-datatype';
import { DataFlowSink } from 'app/model/dataflow/dataflow-sink';
import { DataFlowSource } from 'app/model/dataflow/dataflow-source';
import { EventHubsSettings, streamingRunMode } from 'app/model/dataflow/dataset-settings/event-hubs-settings';
import { DataFlowDSLAdapterRegistry, Metamodel, Owned } from 'shell-models-lib';
import { DataFlowExpressionUtils } from '../expression.util';
import { BaseFormatAdapter } from './base-format.adapter';
import { DslConnectorConstants } from '../../mappingArtifact/DslConnectorConstants';
import { isTruthy } from 'app/model/dataflow/dataflow-expression-utilities';
@DataFlowDSLAdapterRegistry.adapter({
    model: Metamodel.modelFor(EventHubsSettings)
})
export class EventHubsAdapter extends BaseFormatAdapter<EventHubsSettings> {
    protected async toSourceDSL(setting: EventHubsSettings): Promise<string[]> {
        const props = [];
        props.push(`store: 'eventhubs'`);
        props.push(`format: '${setting.format.expression}'`);
        if (setting.instanceSelectionType.expression === 'manual') {
            if (!!setting.eventHubsName?.expression) {
                props.push(`eventHubName: '${setting.eventHubsName.expression}'`);
            }
        } else if (!!setting.eventHubsNamePattern?.expression) {
            props.push(`eventHubNamePattern: '${setting.eventHubsNamePattern.expression}'`);
        }
        if (setting.format.expression !== 'passthrough') {
            const schemaInferenceType =
                setting.format.expression === 'avro' || !isTruthy(setting.isAutoInfer)
                    ? DslConnectorConstants.payloadSchema
                    : DslConnectorConstants.autoInferSchema;
            props.push(`schemaInferenceType: '${schemaInferenceType}'`);
            if (schemaInferenceType === DslConnectorConstants.payloadSchema && setting.payloadSchema?.expression) {
                props.push(`payloadSchema: '${setting.payloadSchema.expression}'`);
            } else if (
                schemaInferenceType === DslConnectorConstants.autoInferSchema &&
                setting.numSampleRecords?.expression
            ) {
                props.push(`numSampleRecords: ${setting.numSampleRecords.expression}`);
            }
        }
        props.push(`includeHeaders: ${setting.includeHeaders.expression}`);
        switch (setting.runMode) {
            case streamingRunMode.Beginning:
                props.push('skipInitialLoad: false');
                break;
            case streamingRunMode.Now:
                props.push('skipInitialLoad: true');
                break;
            case streamingRunMode.Timestamp:
                props.push('skipInitialLoad: false');
                props.push(
                    `seekToTimestamp: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                        setting.seekToTimestamp,
                        TimeStampDataFlowType.TYPE
                    )}`
                );
                break;
            default:
        }
        if (setting.isSinkSchemaQuery) {
            props.push(`isSinkSchemaQuery: ${setting.isSinkSchemaQuery}`);
        }
        props.push(`enableCdc: true`);
        if (setting.format.expression === 'delimited') {
            props.push(`rowDelimiter: ${DataFlowExpressionUtils.interpolateExpression('\\n')}`);
            props.push(`columnDelimiter: ${DataFlowExpressionUtils.interpolateExpression(',')}`);
            props.push(`escapeChar: ${DataFlowExpressionUtils.interpolateExpression('\\\\')}`);
            props.push(`quoteChar: ${DataFlowExpressionUtils.interpolateExpression('\\"')}`);
            props.push(`columnNamesAsHeader: true`);
        }
        return props;
    }
    protected async toSourceModel(
        owner: DataFlowSource,
        configs: Map<string, string>,
        handledConfigs: string[]
    ): Promise<EventHubsSettings> {
        const settings = this._generateSetting(owner);
        if (configs.has('format')) {
            settings.format = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('format'),
                owner,
                StringDataFlowType.TYPE
            );
            handledConfigs.push('format');
        }
        if (configs.has('eventHubName')) {
            settings.eventHubsName = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('eventHubName'),
                owner,
                StringDataFlowType.TYPE
            );
            settings.instanceSelectionType = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                `'manual'`,
                owner,
                StringDataFlowType.TYPE
            );
            handledConfigs.push('eventHubName');
        } else if (configs.has('eventHubNamePattern')) {
            settings.eventHubsNamePattern = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('eventHubNamePattern'),
                owner,
                StringDataFlowType.TYPE
            );
            settings.instanceSelectionType = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                `'pattern'`,
                owner,
                StringDataFlowType.TYPE
            );
            handledConfigs.push('eventHubNamePattern');
        }
        if (configs.has('schemaInferenceType')) {
            const isAutoInfer =
                configs.get('schemaInferenceType') === `'${DslConnectorConstants.autoInferSchema}'` ? 'true' : 'false';
            settings.isAutoInfer = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                isAutoInfer,
                owner,
                BooleanDataFlowType.TYPE
            );
            handledConfigs.push('schemaInferenceType');
        }
        if (settings.isAutoInfer?.expression === 'false' && configs.has('payloadSchema')) {
            settings.payloadSchema = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('payloadSchema'),
                owner,
                StringDataFlowType.TYPE
            );
            handledConfigs.push('payloadSchema');
        }
        if (configs.has('numSampleRecords')) {
            settings.numSampleRecords = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('numSampleRecords'),
                owner,
                NumberDataFlowType.TYPE
            );
            handledConfigs.push('numSampleRecords');
        }
        if (configs.has('includeHeaders')) {
            settings.includeHeaders = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('includeHeaders'),
                owner,
                BooleanDataFlowType.TYPE
            );
            handledConfigs.push('includeHeaders');
        }
        if (configs.has('skipInitialLoad')) {
            settings.skipInitialLoad = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('skipInitialLoad'),
                owner,
                BooleanDataFlowType.TYPE
            );
            handledConfigs.push('skipInitialLoad');
        }
        if (isTruthy(settings.skipInitialLoad)) {
            settings.runMode = streamingRunMode.Now;
        } else if (configs.has('seekToTimestamp')) {
            settings.seekToTimestamp = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('seekToTimestamp'),
                owner,
                TimeStampDataFlowType.TYPE
            );
            settings.runMode = streamingRunMode.Timestamp;
        } else {
            settings.runMode = streamingRunMode.Beginning;
        }
        return settings;
    }

    protected async toSinkDSL(setting: EventHubsSettings): Promise<string[]> {
        const props = [];
        props.push(`store: 'eventhubs'`);
        props.push(`format: '${setting.format.expression}'`);
        props.push(`eventHubName: '${setting.eventHubsName.expression}'`);
        if (setting.format.expression !== 'passthrough') {
            const schemaInferenceType =
                setting.format.expression === 'avro' || !isTruthy(setting.isAutoInfer)
                    ? DslConnectorConstants.payloadSchema
                    : DslConnectorConstants.autoInferSchema;
            props.push(`schemaInferenceType: '${schemaInferenceType}'`);
            if (schemaInferenceType === DslConnectorConstants.payloadSchema && setting.payloadSchema?.expression) {
                props.push(`payloadSchema: '${setting.payloadSchema.expression}'`);
            } else if (
                schemaInferenceType === DslConnectorConstants.autoInferSchema &&
                setting.numSampleRecords?.expression
            ) {
                props.push(`numSampleRecords: ${setting.numSampleRecords.expression}`);
            }
        }
        props.push(`includeHeaders: ${setting.includeHeaders.expression}`);
        return props;
    }

    protected async toSinkModel(
        owner: DataFlowSink,
        configs: Map<string, string>,
        handledConfigs: string[]
    ): Promise<EventHubsSettings> {
        const settings = this._generateSetting(owner);
        if (configs.has('format')) {
            settings.format = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('format'),
                owner,
                StringDataFlowType.TYPE
            );
            handledConfigs.push('format');
        }
        if (configs.has('eventHubName')) {
            settings.eventHubsName = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('eventHubName'),
                owner,
                StringDataFlowType.TYPE
            );
            handledConfigs.push('eventHubName');
        }
        if (configs.has('schemaInferenceType')) {
            const isAutoInfer =
                configs.get('schemaInferenceType') === `'${DslConnectorConstants.autoInferSchema}'` ? 'true' : 'false';
            settings.isAutoInfer = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                isAutoInfer,
                owner,
                BooleanDataFlowType.TYPE
            );
            handledConfigs.push('schemaInferenceType');
        }
        if (settings.isAutoInfer?.expression === 'false' && configs.has('payloadSchema')) {
            settings.payloadSchema = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('payloadSchema'),
                owner,
                StringDataFlowType.TYPE
            );
            handledConfigs.push('payloadSchema');
        }
        if (configs.has('numSampleRecords')) {
            settings.numSampleRecords = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('numSampleRecords'),
                owner,
                NumberDataFlowType.TYPE
            );
            handledConfigs.push('numSampleRecords');
        }
        if (configs.has('includeHeaders')) {
            settings.includeHeaders = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('includeHeaders'),
                owner,
                BooleanDataFlowType.TYPE
            );
            handledConfigs.push('includeHeaders');
        }
        return settings;
    }

    protected _generateSetting(owner: DataFlowSource | DataFlowSink): EventHubsSettings {
        const setting = Owned.create<EventHubsSettings>(EventHubsSettings.MODEL, owner);
        setting.initialize(undefined);
        return setting;
    }
}
